import type { SxProps, Theme } from '@mui/material/styles';
import type { TimeFieldProps as MuiTimeFieldProps } from '@mui/x-date-pickers/TimeField';
import { TimeField as MuiTimeField } from '@mui/x-date-pickers/TimeField';
import type {
  ComponentPropsWithoutRef,
  FocusEventHandler,
  ReactNode,
} from 'react';
import { forwardRef } from 'react';

import Autocomplete from './AutocompleteInput';

const DEFAUT_MINUTES_STEP = 15;

function generateTimes(stepMinutes: number) {
  const times = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += stepMinutes) {
      const hh = String(h).padStart(2, '0');
      const mm = String(m).padStart(2, '0');
      times.push(`${hh}:${mm}`);
    }
  }
  return times.map((t) => ({ label: t, value: t }));
}

interface TimeSelectFieldProps
  extends Omit<ComponentPropsWithoutRef<typeof Autocomplete>, 'options'> {
  minutesStep?: number;
}

export const TimeSelectInput = forwardRef<HTMLDivElement, TimeSelectFieldProps>(
  (props, ref) => {
    const { minutesStep, ...rest } = props;
    return (
      <Autocomplete
        ref={ref}
        fullWidth
        clearOnEscape
        disableClearable={false}
        openOnFocus
        autoHighlight
        options={generateTimes(minutesStep || DEFAUT_MINUTES_STEP)}
        {...rest}
      />
    );
  },
);

TimeSelectInput.displayName = 'TimeSelectField';

type TimeFieldProps = MuiTimeFieldProps & {
  onBlur?: FocusEventHandler;
  required?: boolean;
  fullWidth?: boolean;
  helperText?: ReactNode;
  error?: boolean;
  sx?: SxProps<Theme>;
};

export const TimeInput = forwardRef<HTMLDivElement, TimeFieldProps>(
  (props, ref) => {
    const {
      onBlur,
      required,
      fullWidth,
      helperText,
      error,
      slotProps,
      ...rest
    } = props;
    return (
      <MuiTimeField
        ref={ref}
        slotProps={{
          textField: {
            onBlur,
            required,
            fullWidth,
            helperText,
            error,
          },
          ...slotProps,
        }}
        {...rest}
      />
    );
  },
);
